import React, { useState, useEffect } from 'react'
import {
  UserCircleIcon,
  CameraIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  ShieldCheckIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import {
  CheckCircleIcon as CheckCircleSolid
} from '@heroicons/react/24/solid'
import { profileService, UserProfile, UpdateProfileData, ChangePasswordData, ProfileCompletion } from '../../services/profile.service'
import { useAuth } from '../../hooks/useAuth'
import MembershipInfoCard from '../../components/profile/MembershipInfoCard'
import MembershipECard from '../../components/profile/MembershipECard'
import BurialPreferenceInput from '../../components/profile/BurialPreferenceInput'
import UserDocumentationTab from '../../components/profile/UserDocumentationTab'
import VerificationStatusBanner from '../../components/profile/VerificationStatusBanner'
import TwoFactorSettings from '../../components/twofa/TwoFactorSettings'
import { ADDRESS_STATE_NAMES, getCitiesForStateName } from '../../constants/malaysian-states'
import { PhoneInput } from '../../components/ui'
import type { BurialCountryCode } from '../../data/burial-locations'

export default function ProfilePage() {
  const { updateUser } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [completion, setCompletion] = useState<ProfileCompletion | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'basic' | 'security' | 'picture' | 'documentation'>('basic')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Form states
  const [formData, setFormData] = useState<UpdateProfileData>({})
  const [passwordData, setPasswordData] = useState<ChangePasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [uploadingPicture, setUploadingPicture] = useState(false)

  useEffect(() => {
    loadProfile()
    loadCompletion()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const data = await profileService.getProfile()
      setProfile(data)
      setFormData({
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber || data.phone || '',
        email: data.email,
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        postalCode: data.postalCode || '',
        emergencyContactName: data.emergencyContactName || '',
        emergencyContactPhone: data.emergencyContactPhone || '',
        dateOfBirth: data.dateOfBirth || '',
        gender: data.gender || undefined,
        preferredBurialLocation: data.preferredBurialLocation || '',
        preferredBurialCountry: data.preferredBurialCountry || 'MY',
        passportNumber: data.passportNumber || '',
        nationality: data.nationality || '',
      })
    } catch (error) {
      console.error('Failed to load profile:', error)
      setErrorMessage('Failed to load profile. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadCompletion = async () => {
    try {
      const data = await profileService.getProfileCompletion()
      setCompletion(data)
    } catch (error) {
      console.error('Failed to load completion status:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
  }

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      const updatedProfile = await profileService.updateProfile(formData)
      setProfile(updatedProfile)
      setSuccessMessage('Profile updated successfully!')
      await loadCompletion()

      // Update user data in auth context and localStorage
      updateUser(updatedProfile)

      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to update profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setErrorMessage('')
    setSuccessMessage('')

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMessage('New passwords do not match.')
      setSaving(false)
      return
    }

    try {
      await profileService.changePassword(passwordData)
      setSuccessMessage('Password changed successfully!')
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to change password. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Only JPEG, PNG, and WebP images are allowed.')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('File size must be less than 5MB.')
      return
    }

    setUploadingPicture(true)
    setErrorMessage('')

    try {
      const updatedProfile = await profileService.uploadProfilePicture(file)
      setProfile(updatedProfile)
      setSuccessMessage('Profile picture updated successfully!')

      // Update user data in auth context and localStorage
      updateUser(updatedProfile)

      await loadCompletion()
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to upload picture. Please try again.')
    } finally {
      setUploadingPicture(false)
    }
  }

  const handleDeletePicture = async () => {
    if (!confirm('Are you sure you want to delete your profile picture?')) return

    try {
      await profileService.deleteProfilePicture()
      if (profile) {
        const updatedProfile = { ...profile, profilePicture: undefined }
        setProfile(updatedProfile)

        // Update user data in auth context and localStorage
        updateUser(updatedProfile)
      }
      setSuccessMessage('Profile picture deleted successfully!')
      await loadCompletion()
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to delete picture. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <h1 className="text-2xl font-bold text-neutral-900">Profile Settings</h1>
          {completion && (
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">Profile Completion</span>
                <span className="text-sm font-medium text-neutral-900">{completion.completionPercentage}%</span>
              </div>
              <div className="mt-1 w-full bg-neutral-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${completion.completionPercentage}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <CheckCircleSolid className="h-5 w-5 text-green-400" />
              <p className="ml-2 text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <p className="ml-2 text-sm text-red-800">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Verification status banner */}
        {profile && (
          <VerificationStatusBanner
            isVerified={profile.isVerified ?? false}
            onGoToDocs={() => setActiveTab('documentation')}
          />
        )}

        {/* Tabs */}
        <div className="border-b border-neutral-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('basic')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap shrink-0 ${
                activeTab === 'basic'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center">
                <UserIcon className="h-5 w-5 mr-2" />
                Basic Information
              </div>
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'security'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center">
                <KeyIcon className="h-5 w-5 mr-2" />
                Security
              </div>
            </button>
            <button
              onClick={() => setActiveTab('picture')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'picture'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center">
                <CameraIcon className="h-5 w-5 mr-2" />
                Profile Picture
              </div>
            </button>
            <button
              onClick={() => setActiveTab('documentation')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documentation'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Documentation
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <form onSubmit={handleSaveProfile} className="space-y-6">
              <MembershipECard
                id={profile?.id}
                memberId={profile?.memberId}
                virtualAccountNumber={profile?.virtualAccountNumber}
                firstName={profile?.firstName}
                lastName={profile?.lastName}
              />
              <MembershipInfoCard
                memberId={profile?.memberId}
                walletNumber={profile?.walletNumber}
                virtualAccountNumber={profile?.virtualAccountNumber}
                virtualAccountBank={profile?.virtualAccountBank}
                vaBalance={profile?.virtualAccountBalance}
                referralCode={profile?.referralCode}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <PhoneInput
                    label="Phone Number"
                    value={formData.phoneNumber || ''}
                    onChange={(value) => setFormData((prev) => ({ ...prev, phoneNumber: value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Date of Birth <span className="text-neutral-400">(Optional)</span></label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Gender <span className="text-neutral-400">(Optional)</span></label>
                  <select
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900">Address</h3>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Street Address</label>
                  <textarea
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">City <span className="text-neutral-400">(Optional)</span></label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleInputChange}
                      list="profile-city-options"
                      className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <datalist id="profile-city-options">
                      {getCitiesForStateName(formData.state || '').map((city) => (
                        <option key={city} value={city} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">State <span className="text-neutral-400">(Optional)</span></label>
                    <select
                      name="state"
                      value={formData.state || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">Select State</option>
                      {ADDRESS_STATE_NAMES.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Postal Code <span className="text-neutral-400">(Optional)</span></label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Contact Name</label>
                    <input
                      type="text"
                      name="emergencyContactName"
                      value={formData.emergencyContactName || ''}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <PhoneInput
                      label="Contact Phone"
                      value={formData.emergencyContactPhone || ''}
                      onChange={(value) => setFormData((prev) => ({ ...prev, emergencyContactPhone: value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900">Burial Preference</h3>
                <BurialPreferenceInput
                  country={(formData.preferredBurialCountry as BurialCountryCode) || 'MY'}
                  location={formData.preferredBurialLocation || ''}
                  onCountryChange={(country) =>
                    setFormData((prev) => ({ ...prev, preferredBurialCountry: country }))
                  }
                  onLocationChange={(value) =>
                    setFormData((prev) => ({ ...prev, preferredBurialLocation: value }))
                  }
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="max-w-md space-y-10">
              <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    className="mt-1 block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
              </form>

              {/* Two-Factor Authentication (TOTP) */}
              <TwoFactorSettings />
            </div>
          )}

          {/* Profile Picture Tab */}
          {activeTab === 'picture' && (
            <div className="max-w-md">
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {profile?.profilePicture ? (
                    <img
                      src={
                        profile.profilePicture.startsWith('data:')
                          ? profile.profilePicture
                          : profileService.getProfilePictureUrl(profile.id)
                      }
                      alt="Profile"
                      className="h-24 w-24 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-neutral-200 flex items-center justify-center">
                      <UserCircleIcon className="h-16 w-16 text-neutral-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="mt-2">
                    <label className="cursor-pointer px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 inline-block">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePictureUpload}
                        disabled={uploadingPicture}
                        className="hidden"
                      />
                      {uploadingPicture ? 'Uploading...' : 'Change Picture'}
                    </label>
                    {profile?.profilePicture && (
                      <button
                        onClick={handleDeletePicture}
                        className="ml-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-4 w-4 inline mr-1" />
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-neutral-500">
                    JPEG, PNG, or WebP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Documentation Tab */}
          {activeTab === 'documentation' && (
            <div className="space-y-6">
              <UserDocumentationTab
                passportNumber={formData.passportNumber}
                onPassportNumberChange={(value) =>
                  setFormData((prev) => ({ ...prev, passportNumber: value }))
                }
              />
              <div className="flex justify-end border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => handleSaveProfile({ preventDefault: () => {} } as React.FormEvent)}
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save Passport / NRIC Number'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}